import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import axiosInstance from '../../services/axiosInstance';
import { getBankAccounts } from '../../services/BankAccountService';
import {
  DEFAULT_NAVIGATION_PREFERENCES,
  HOME_BANK_ACCOUNT,
  HOME_OPTIONS,
} from '../../utils/homePage';

const HomePagePreferences = () => {
  const [prefs, setPrefs] = useState(DEFAULT_NAVIGATION_PREFERENCES);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [profileRes, accounts] = await Promise.all([
          axiosInstance.get('/user/profile/'),
          getBankAccounts(),
        ]);
        setPrefs(
          profileRes.data.navigation_preferences || DEFAULT_NAVIGATION_PREFERENCES
        );
        setBankAccounts(accounts || []);
      } catch (error) {
        console.error('Error loading home page preferences:', error);
        setErrorMessage('Failed to load home page settings.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleHomeChange = (platform, value) => {
    setPrefs((prev) => ({
      ...prev,
      [`${platform}_home`]: value,
      ...(value !== HOME_BANK_ACCOUNT
        ? { [`${platform}_home_bank_account_id`]: null }
        : {}),
    }));
  };

  const handleBankChange = (platform, value) => {
    const parsed = value === '' ? null : parseInt(value, 10);
    setPrefs((prev) => ({
      ...prev,
      [`${platform}_home_bank_account_id`]: parsed,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      await axiosInstance.patch('/user/profile/', prefs);
      setSuccessMessage('Home page settings saved.');
      window.dispatchEvent(new Event('homePreferencesUpdated'));
    } catch (error) {
      const data = error.response?.data;
      if (data && typeof data === 'object') {
        const firstKey = Object.keys(data)[0];
        setErrorMessage(
          Array.isArray(data[firstKey]) ? data[firstKey][0] : String(data[firstKey])
        );
      } else {
        setErrorMessage('Failed to save home page settings.');
      }
    } finally {
      setSaving(false);
    }
  };

  const renderPlatformForm = (platform, title, description) => {
    const homeKey = `${platform}_home`;
    const bankKey = `${platform}_home_bank_account_id`;
    const homeValue = prefs[homeKey];

    return (
      <Col md={6} key={platform}>
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">{title}</h5>
          </Card.Header>
          <Card.Body>
            <p className="text-muted small">{description}</p>
            <Form.Group className="mb-3">
              <Form.Label>Home page</Form.Label>
              <Form.Select
                value={homeValue}
                onChange={(e) => handleHomeChange(platform, e.target.value)}
              >
                {HOME_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            {homeValue === HOME_BANK_ACCOUNT && (
              <Form.Group className="mb-0">
                <Form.Label>Bank account</Form.Label>
                <Form.Select
                  value={prefs[bankKey] ?? ''}
                  onChange={(e) => handleBankChange(platform, e.target.value)}
                >
                  <option value="">Select an account…</option>
                  {bankAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.nickname || account.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
          </Card.Body>
        </Card>
      </Col>
    );
  };

  if (loading) {
    return <p>Loading home page settings…</p>;
  }

  return (
    <div className="home-page-preferences">
      <h5 className="mb-3">Home page</h5>
      <p className="text-muted">
        Choose where you land after signing in and where the logo takes you when
        tapped. Desktop and mobile can be set separately.
      </p>

      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage('')} dismissible>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert variant="danger" onClose={() => setErrorMessage('')} dismissible>
          {errorMessage}
        </Alert>
      )}

      <Row>
        {renderPlatformForm(
          'desktop',
          'Desktop',
          'Used when you sign in or tap the logo on a wider screen.'
        )}
        {renderPlatformForm(
          'mobile',
          'Mobile',
          'Used when you sign in or tap the logo on a phone (portrait or landscape).'
        )}
      </Row>

      <Button variant="primary" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save home page settings'}
      </Button>
    </div>
  );
};

export default HomePagePreferences;
